import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, type SecretMessage } from '../lib/api-client';

/**
 * Hook for managing secret messages in streaming rooms
 * @param scheduleId - Event schedule ID to filter messages by schedule
 */
export function useSecretMessages(streamKey: string, isEnabled: boolean, shouldFetch: boolean = true, scheduleId?: string) {
    const { i18n } = useTranslation();
    const [messages, setMessages] = useState<SecretMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pollingRef = useRef<number | null>(null);

    // Get user's preferred language for translation
    const userLang = i18n.language === 'en' ? 'en' : 'ja';

    const fetchMessages = useCallback(async () => {
        if (!shouldFetch) {
            console.log('[SecretMessages] Fetch skipped - shouldFetch is false');
            return;
        }
        if (!streamKey) {
            console.log('[SecretMessages] Fetch skipped - streamKey is empty');
            return;
        }
        try {
            console.log('[SecretMessages] Fetching messages for streamKey:', streamKey, 'lang:', userLang);
            const msgs = await apiClient.getSecretMessages(streamKey, 50, scheduleId, userLang);
            console.log('[SecretMessages] Fetched', msgs.length, 'messages');
            setMessages(msgs);
            setError(null);
        } catch (err) {
            console.error('[SecretMessages] Failed to fetch secret messages:', err);
            // Don't set error state to avoid UI flickering during polling
        }
    }, [streamKey, shouldFetch, scheduleId, userLang]);

    useEffect(() => {
        console.log('[SecretMessages] Effect - isEnabled:', isEnabled, 'streamKey:', streamKey, 'shouldFetch:', shouldFetch);
        if (!isEnabled || !streamKey || !shouldFetch) {
            // Clear messages when disabled to prevent stale data on reconnection
            console.log('[SecretMessages] Clearing messages - conditions not met');
            setMessages([]);
            if (pollingRef.current) {
                window.clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }

        // Initial fetch
        console.log('[SecretMessages] Starting initial fetch and polling');
        setIsLoading(true);
        fetchMessages().finally(() => setIsLoading(false));

        // Start polling (every 5 seconds)
        pollingRef.current = window.setInterval(fetchMessages, 5000);

        return () => {
            if (pollingRef.current) {
                window.clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [isEnabled, streamKey, shouldFetch, fetchMessages]);

    const sendMessage = useCallback(async (message: string) => {
        try {
            setError(null);

            // Try to consume points first (assuming streamer username is needed, but we don't have it here easily)
            // Ideally the backend should handle point consumption on message send.
            // For now, we'll just send the message. If point consumption is needed client-side,
            // we need the streamer's username.

            await apiClient.sendSecretMessage(streamKey, message, undefined, scheduleId, userLang);

            // Refresh messages immediately
            await fetchMessages();
        } catch (err) {
            console.error('Failed to send secret message:', err);
            setError(err instanceof Error ? err.message : 'メッセージの送信に失敗しました');
            throw err;
        }
    }, [streamKey, fetchMessages, scheduleId, userLang]);

    return {
        messages,
        sendMessage,
        isLoading,
        error,
        refresh: fetchMessages
    };
}
